/**
 * How the habit list is organised (Habits UX pass 2026-09-06). A habit list
 * is read for "which are due today, are they done, how am I keeping up", so
 * the default grouping is by today and the week, not by the goal:
 *
 *   Today             an occurrence today — still to do first, then done, then skipped
 *   This week         the next one comes within seven days
 *   Later             scheduled, but nothing within the week
 *   Needs a schedule  active, no rule and no time — the next step is to set one
 *   Paused            paused by the person or by Auto-Mom, or parked by rationing
 *   Archived          retired — collapsed
 *
 * Three status axes reduce to one line of words here (`habitState`), with a
 * fixed precedence: Archived › Paused › Parked › the formation stage.
 *
 * Pure functions with no runtime imports so node can probe them
 * (`npm run test:habits`); the page injects the life-area labeller.
 */
export type HabitSectionKey = 'today' | 'week' | 'later' | 'unscheduled' | 'paused' | 'archived'
export type HabitGroupBy = 'when' | 'goal' | 'area' | 'none'
export type HabitSortBy = 'attention' | 'streak' | 'created' | 'updated'
export type SortDir = 'asc' | 'desc'
export interface SortSpec { by: HabitSortBy; dir: SortDir }

/** The fields the rules read; the page's Habit type satisfies it. */
export interface HabitLike {
  id: number | string
  title: string
  status?: string | null
  habit_status?: string | null
  is_active?: boolean | null
  paused_until?: string | null
  archived_at?: string | null
  rrule?: string | null
  scheduled_times_local?: string[] | null
  preferred_start_time?: string | null
  preferred_days?: string[] | null
  target_cadence?: string | null
  created_at?: string | null
  updated_at?: string | null
  life_area?: string | null
  parent?: { type: string; id: number; title: string | null; life_area?: string | null } | null
}

export interface CardLike {
  today?: { outcome: string; scheduled_at?: string | null; at?: string | null } | null
  next?: { date: string; at?: string | null } | null
  streak?: number
}

export interface HabitGroup<H extends HabitLike = HabitLike> {
  key: string
  title: string
  quiet?: string
  items: H[]
  collapsedByDefault?: boolean
}

export const SECTION_TITLE: Record<HabitSectionKey, string> = {
  today: 'Today',
  week: 'This week',
  later: 'Later',
  unscheduled: 'Needs a schedule',
  paused: 'Paused',
  archived: 'Archived',
}
const SECTION_ORDER: HabitSectionKey[] = ['today', 'week', 'later', 'unscheduled', 'paused', 'archived']

export type HabitStateKey = 'archived' | 'paused' | 'parked' | 'forming' | 'steady' | 'second_nature' | 'active'
export interface HabitState { key: HabitStateKey; words: string | null; pausedUntil: string | null }

const STAGE_WORDS: Record<string, [HabitStateKey, string]> = {
  forming: ['forming', 'Forming'],
  stable: ['steady', 'Steady'],
  internalized: ['second_nature', 'Second nature'],
}

/** One line of words for the three status axes. */
export function habitState(h: HabitLike): HabitState {
  if (h.archived_at) return { key: 'archived', words: 'Archived', pausedUntil: null }
  if (h.paused_until || h.is_active === false || h.habit_status === 'paused') {
    return { key: 'paused', words: 'Paused', pausedUntil: h.paused_until || null }
  }
  if (h.status === 'parked') return { key: 'parked', words: 'Parked', pausedUntil: null }
  const stage = STAGE_WORDS[String(h.habit_status || '')]
  if (stage) return { key: stage[0], words: stage[1], pausedUntil: null }
  return { key: 'active', words: null, pausedUntil: null }
}

export const isRetired = (h: HabitLike): boolean => Boolean(h.archived_at)
export const isResting = (h: HabitLike): boolean => { const k = habitState(h).key; return k === 'paused' || k === 'parked' }

/** True when the habit has said anything about when it comes round. */
export function hasSchedule(h: HabitLike): boolean {
  if (h.rrule && String(h.rrule).trim()) return true
  if (h.preferred_start_time) return true
  if (h.preferred_days && h.preferred_days.length > 0) return true
  return Boolean(h.target_cadence)
}

/** Whole local days from `today` (YYYY-MM-DD) to a YYYY-MM-DD; null when unreadable. */
export function daysBetween(fromKey: string, toKey: string): number | null {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(fromKey), b = /^(\d{4})-(\d{2})-(\d{2})/.exec(toKey)
  if (!a || !b) return null
  const da = new Date(Number(a[1]), Number(a[2]) - 1, Number(a[3]))
  const db = new Date(Number(b[1]), Number(b[2]) - 1, Number(b[3]))
  return Math.round((db.getTime() - da.getTime()) / 86_400_000)
}

export function sectionOf(h: HabitLike, card: CardLike | null | undefined, todayKey: string): HabitSectionKey {
  if (isRetired(h)) return 'archived'
  if (isResting(h)) return 'paused'
  if (card?.today) return 'today'
  if (card?.next) {
    const d = daysBetween(todayKey, card.next.date)
    return d != null && d <= 7 ? 'week' : 'later'
  }
  return hasSchedule(h) ? 'later' : 'unscheduled'
}

const cmp = (a: string | number, b: string | number) => (a < b ? -1 : a > b ? 1 : 0)
const TODAY_RANK: Record<string, number> = { pending: 0, done: 1, missed: 2, skipped: 3 }

export function sortSection<H extends HabitLike>(items: H[], key: HabitSectionKey, cards: Record<string, CardLike | undefined>): H[] {
  const card = (h: H) => cards[String(h.id)]
  const arr = [...items]
  switch (key) {
    case 'today':
      return arr.sort((a, b) =>
        cmp(TODAY_RANK[card(a)?.today?.outcome || 'pending'] ?? 9, TODAY_RANK[card(b)?.today?.outcome || 'pending'] ?? 9)
        || cmp(String(card(a)?.today?.scheduled_at || ''), String(card(b)?.today?.scheduled_at || ''))
        || cmp(a.title.toLowerCase(), b.title.toLowerCase()))
    case 'week':
    case 'later':
      return arr.sort((a, b) =>
        cmp(String(card(a)?.next?.date || '9999') + String(card(a)?.next?.at || ''), String(card(b)?.next?.date || '9999') + String(card(b)?.next?.at || ''))
        || cmp(a.title.toLowerCase(), b.title.toLowerCase()))
    case 'unscheduled':
      return arr.sort((a, b) => cmp(String(b.created_at || ''), String(a.created_at || '')))
    case 'paused':
      return arr.sort((a, b) => cmp(String(a.paused_until || '9999'), String(b.paused_until || '9999')) || cmp(a.title.toLowerCase(), b.title.toLowerCase()))
    case 'archived':
      return arr.sort((a, b) => cmp(String(b.archived_at || ''), String(a.archived_at || '')))
  }
}

export function timeKey(h: HabitLike, by: 'created' | 'updated'): string {
  return String((by === 'created' ? (h.created_at || h.updated_at) : (h.updated_at || h.created_at)) || '')
}

export function sortByTime<H extends HabitLike>(items: H[], by: 'created' | 'updated', dir: SortDir): H[] {
  return [...items].sort((a, b) => {
    const ka = timeKey(a, by), kb = timeKey(b, by)
    if (!ka || !kb) return cmp(ka ? 0 : 1, kb ? 0 : 1)
    return dir === 'desc' ? cmp(kb, ka) : cmp(ka, kb)
  })
}

export function sortByStreak<H extends HabitLike>(items: H[], cards: Record<string, CardLike | undefined>, dir: SortDir): H[] {
  return [...items].sort((a, b) => {
    const sa = cards[String(a.id)]?.streak ?? 0, sb = cards[String(b.id)]?.streak ?? 0
    return (dir === 'desc' ? cmp(sb, sa) : cmp(sa, sb)) || cmp(a.title.toLowerCase(), b.title.toLowerCase())
  })
}

export function matchesQuery(h: HabitLike & { description?: string | null; life_areas?: string[] | null; cue_or_anchor?: string | null }, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const hay = [h.title, h.description, h.parent?.title, h.life_area, ...(h.life_areas || []), h.cue_or_anchor].filter(Boolean).join(' ').toLowerCase()
  return q.split(/\s+/).every((w) => hay.includes(w))
}

export interface HabitSummary { total: number; dueToday: number; doneToday: number; resting: number; bestRun: number }

export function summarize(habits: HabitLike[], cards: Record<string, CardLike | undefined>, todayKey: string): HabitSummary {
  const s: HabitSummary = { total: 0, dueToday: 0, doneToday: 0, resting: 0, bestRun: 0 }
  for (const h of habits) {
    if (isRetired(h)) continue
    s.total += 1
    const k = sectionOf(h, cards[String(h.id)], todayKey)
    if (k === 'paused') s.resting += 1
    if (k === 'today') {
      s.dueToday += 1
      if (cards[String(h.id)]?.today?.outcome === 'done') s.doneToday += 1
    }
    s.bestRun = Math.max(s.bestRun, cards[String(h.id)]?.streak ?? 0)
  }
  return s
}

export function summaryWords(s: HabitSummary): string {
  if (s.total === 0) return 'Nothing here yet'
  const parts = [`${s.total} habit${s.total === 1 ? '' : 's'}`]
  if (s.dueToday) parts.push(`${s.dueToday} due today${s.doneToday ? `, ${s.doneToday} done` : ''}`)
  else parts.push('nothing due today')
  if (s.bestRun >= 2) parts.push(`longest run ${s.bestRun}`)
  if (s.resting) parts.push(`${s.resting} paused`)
  return parts.join(' · ')
}

const attentionOrder = <H extends HabitLike>(cards: Record<string, CardLike | undefined>, todayKey: string) => (a: H, b: H) => {
  const ka = sectionOf(a, cards[String(a.id)], todayKey), kb = sectionOf(b, cards[String(b.id)], todayKey)
  if (ka !== kb) return cmp(SECTION_ORDER.indexOf(ka), SECTION_ORDER.indexOf(kb))
  return sortSection([a, b], ka, cards)[0] === a ? -1 : 1
}

export function groupHabits<H extends HabitLike>(
  habits: H[], cards: Record<string, CardLike | undefined>, by: HabitGroupBy, todayKey: string,
  labelArea: (value: string) => string = (v) => v, sort: SortSpec = { by: 'attention', dir: 'desc' },
): HabitGroup<H>[] {
  const groups = buildGroups(habits, cards, by, todayKey, labelArea)
  if (sort.by === 'attention') return groups
  if (sort.by === 'streak') return groups.map((g) => ({ ...g, items: sortByStreak(g.items, cards, sort.dir) }))
  return groups.map((g) => ({ ...g, items: sortByTime(g.items, sort.by as 'created' | 'updated', sort.dir) }))
}

function buildGroups<H extends HabitLike>(habits: H[], cards: Record<string, CardLike | undefined>, by: HabitGroupBy, todayKey: string, labelArea: (v: string) => string): HabitGroup<H>[] {
  if (by === 'when') {
    const buckets: Record<HabitSectionKey, H[]> = { today: [], week: [], later: [], unscheduled: [], paused: [], archived: [] }
    for (const h of habits) buckets[sectionOf(h, cards[String(h.id)], todayKey)].push(h)
    const anyLive = SECTION_ORDER.some((k) => k !== 'archived' && k !== 'paused' && buckets[k].length > 0)
    return SECTION_ORDER
      .map((k): HabitGroup<H> => ({
        key: k, title: SECTION_TITLE[k], items: sortSection(buckets[k], k, cards),
        quiet: k === 'today' && anyLive ? 'Nothing is due today. A rest day is part of the pattern.' : undefined,
        collapsedByDefault: k === 'archived',
      }))
      .filter((g) => g.items.length > 0 || g.quiet)
  }
  const live = habits.filter((h) => !isRetired(h))
  const retired = sortSection(habits.filter(isRetired), 'archived', cards)
  if (by === 'none') {
    const groups: HabitGroup<H>[] = []
    if (live.length) groups.push({ key: 'all', title: 'Habits', items: [...live].sort(attentionOrder(cards, todayKey)) })
    if (retired.length) groups.push({ key: 'archived', title: SECTION_TITLE.archived, items: retired, collapsedByDefault: true })
    return groups
  }
  const map = new Map<string, HabitGroup<H>>()
  const orphanKey = by === 'goal' ? 'none' : 'area:none'
  for (const h of live) {
    let key = orphanKey
    let title = by === 'goal' ? 'On their own' : 'No life area'
    if (by === 'goal' && h.parent?.title) { key = `${h.parent.type}:${h.parent.id}`; title = h.parent.title }
    if (by === 'area') {
      const area = h.life_area || h.parent?.life_area || null
      if (area) { key = `area:${area}`; title = labelArea(area) }
    }
    if (!map.has(key)) map.set(key, { key, title, items: [] })
    map.get(key)!.items.push(h)
  }
  const groups = [...map.values()]
  for (const g of groups) g.items.sort(attentionOrder(cards, todayKey))
  groups.sort((a, b) => cmp(a.key === orphanKey ? 1 : 0, b.key === orphanKey ? 1 : 0) || cmp(a.title.toLowerCase(), b.title.toLowerCase()))
  if (retired.length) groups.push({ key: 'archived', title: SECTION_TITLE.archived, items: retired, collapsedByDefault: true })
  return groups
}
